import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  cn,
  Modal,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
} from '@playerbase/ui';
import type { FeedbackForm } from '@playerbase/types';

import {
  archiveFeedbackForm,
  deleteFeedbackForm,
  requestFormRepost,
  unarchiveFeedbackForm,
  type FeedbackFormWithCount,
} from '../../games/feedback-forms-data.js';
import { useShell } from '../../shell/use-shell.js';
import { DisableConfirmModal } from './builder/disable-confirm-modal.js';

// Mirrors the server-side cooldown enforced by request_form_repost
// (migration 0034). Server is authoritative; this just keeps the button
// disabled so the typical case never hits the SQL guard.
const REPOST_COOLDOWN_MS = 30_000;

interface FormRowProps {
  row: FeedbackFormWithCount;
}

const STATUS_TONE: Record<FeedbackForm['status'], 'neutral' | 'success' | 'warning'> = {
  draft: 'neutral',
  enabled: 'success',
  disabled: 'warning',
};

const STATUS_LABEL: Record<FeedbackForm['status'], string> = {
  draft: 'Draft',
  enabled: 'Enabled',
  disabled: 'Disabled',
};

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const seconds = Math.max(0, (Date.now() - then) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

export function FormRow({ row }: FormRowProps): JSX.Element {
  const { form, submissionsCount } = row;
  const navigate = useNavigate();
  const params = useParams<{ studioShortId: string; gameShortId: string }>();
  const { refreshFeedbackForms } = useShell();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [disableOpen, setDisableOpen] = useState(false);
  const [repostInFlight, setRepostInFlight] = useState(false);
  const [repostError, setRepostError] = useState<string | null>(null);
  const [archiveInFlight, setArchiveInFlight] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  // Epoch ms when the cooldown expires. null = no cooldown active. We
  // tick a 1s interval while it's set so the button label can render a
  // live countdown ("Wait 28s…") instead of a generic disabled state.
  const [cooldownEndsAt, setCooldownEndsAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (cooldownEndsAt === null) return;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [cooldownEndsAt]);

  useEffect(() => {
    if (cooldownEndsAt !== null && now >= cooldownEndsAt) {
      setCooldownEndsAt(null);
    }
  }, [now, cooldownEndsAt]);

  const cooldownSecondsLeft =
    cooldownEndsAt !== null ? Math.max(0, Math.ceil((cooldownEndsAt - now) / 1000)) : 0;

  function openBuilder(): void {
    if (params.studioShortId === undefined || params.gameShortId === undefined) return;
    navigate(
      `/s/${params.studioShortId}/g/${params.gameShortId}/feedback-settings/forms/${form.id}`,
    );
  }

  async function handleDelete(): Promise<void> {
    setBusy(true);
    setDeleteError(null);
    const result = await deleteFeedbackForm(form.id);
    if (!result.ok) {
      setBusy(false);
      setDeleteError(
        result.error === 'form_has_submissions'
          ? 'This form has responses and cannot be deleted.'
          : result.error === 'form_enabled_cannot_delete'
            ? 'Disable the form before deleting it.'
            : 'Could not delete the form. Please try again.',
      );
      return;
    }
    await refreshFeedbackForms();
    setBusy(false);
    setConfirmOpen(false);
  }

  async function handleArchive(): Promise<void> {
    setArchiveInFlight(true);
    setArchiveError(null);
    const result = await archiveFeedbackForm(form.id);
    setArchiveInFlight(false);
    if (!result.ok) {
      setArchiveError('Could not archive the form. Please try again.');
      return;
    }
    await refreshFeedbackForms();
  }

  async function handleUnarchive(): Promise<void> {
    setArchiveInFlight(true);
    setArchiveError(null);
    const result = await unarchiveFeedbackForm(form.id);
    setArchiveInFlight(false);
    if (!result.ok) {
      setArchiveError('Could not unarchive the form. Please try again.');
      return;
    }
    await refreshFeedbackForms();
  }

  async function handleRepost(): Promise<void> {
    setRepostInFlight(true);
    setRepostError(null);
    const result = await requestFormRepost(form.id);
    setRepostInFlight(false);
    if (!result.ok) {
      setRepostError(
        result.error === 'repost_cooldown'
          ? 'Wait a moment before reposting again.'
          : result.error === 'form_not_enabled'
            ? 'Enable the form first.'
            : 'Could not repost the form. Please try again.',
      );
      return;
    }
    // Hold the disabled state for the full cooldown window. Server is
    // still authoritative; this just keeps double-clicks off the wire
    // and lets the button render a live countdown.
    setCooldownEndsAt(Date.now() + REPOST_COOLDOWN_MS);
    setNow(Date.now());
  }

  // PR 4 wires Disable for enabled forms; Delete is gated on disabled /
  // draft status (the RPC also enforces this server-side). PR 6 adds
  // Repost now for enabled forms (kebab). Archive (migration 0047)
  // applies to anything not already archived; archiving an enabled form
  // disables it as a side effect via the server-side RPC.
  const archived = form.archivedAt !== null;
  const hasSubmissions = submissionsCount > 0;
  const canDelete = form.status !== 'enabled' && !hasSubmissions;
  const canDisable = form.status === 'enabled';
  const canRepost = form.status === 'enabled';
  // Tooltip surfaces only when the row is disabled because of
  // submissions; if the form is enabled the visible Disable action
  // already explains the gating, no extra hint needed.
  const deleteBlockReason = hasSubmissions
    ? "Can't delete forms that have submissions"
    : null;
  // For enabled forms, prefer the repost timestamp if we have one - it's
  // closer to the user's mental model ("when did this form's button last
  // freshen?") than updatedAt, which moves on every metadata edit.
  const timestampLine =
    form.status === 'enabled' && form.lastRepostedAt !== null
      ? `Reposted ${relativeTime(form.lastRepostedAt)}`
      : `Updated ${relativeTime(form.updatedAt)}`;

  return (
    <li>
      {/* The row container is a <div> rather than a <button> so the kebab
          popover (also a <button>) is not a descendant of the row's
          clickable surface. The earlier nested-button structure tripped
          DOM validation and confused click event flow. The row's title
          area is its own <button> sibling to the kebab. */}
      <div
        className={cn(
          'flex items-center justify-between gap-4 px-4 py-3 transition-colors hover:bg-surface-3',
          archived && 'opacity-60',
        )}
      >
        <button
          type="button"
          onClick={openBuilder}
          className="-mx-2 -my-1 flex min-w-0 flex-1 flex-col gap-1 rounded-md px-2 py-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <span className="flex items-center gap-2">
            <span className="truncate font-medium text-text">{form.name}</span>
            <Badge tone={STATUS_TONE[form.status]} aria-label={`Status: ${STATUS_LABEL[form.status]}`}>
              {STATUS_LABEL[form.status]}
            </Badge>
            {archived ? <Badge>Archived</Badge> : null}
          </span>
          {form.internalDescription !== null ? (
            <span className="truncate text-xs text-text-muted">{form.internalDescription}</span>
          ) : null}
        </button>

        <span className="hidden text-xs text-text-muted sm:inline">
          {timestampLine}
        </span>

        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Form actions"
              className="rounded-md px-2 py-1 text-text-muted hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              ⋯
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="min-w-[10rem]">
            {canRepost && !archived ? (
              <>
                <button
                  type="button"
                  disabled={repostInFlight || cooldownEndsAt !== null}
                  onClick={() => void handleRepost()}
                  className="block w-full rounded-sm px-3 py-2 text-left text-sm text-text hover:bg-surface-3 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {repostInFlight
                    ? 'Posting…'
                    : cooldownEndsAt !== null
                      ? `Wait ${cooldownSecondsLeft}s…`
                      : 'Repost now'}
                </button>
                {repostError !== null ? (
                  <p className="px-3 pb-2 text-xs text-danger-text">{repostError}</p>
                ) : null}
              </>
            ) : null}
            {canDisable && !archived ? (
              <button
                type="button"
                onClick={() => setDisableOpen(true)}
                className="block w-full rounded-sm px-3 py-2 text-left text-sm text-text hover:bg-surface-3"
              >
                Disable form
              </button>
            ) : null}
            {archived ? (
              <button
                type="button"
                disabled={archiveInFlight}
                onClick={() => void handleUnarchive()}
                className="block w-full rounded-sm px-3 py-2 text-left text-sm text-text hover:bg-surface-3 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {archiveInFlight ? 'Unarchiving…' : 'Unarchive form'}
              </button>
            ) : (
              <button
                type="button"
                disabled={archiveInFlight}
                onClick={() => void handleArchive()}
                className="block w-full rounded-sm px-3 py-2 text-left text-sm text-text hover:bg-surface-3 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {archiveInFlight ? 'Archiving…' : 'Archive form'}
              </button>
            )}
            {archiveError !== null ? (
              <p className="px-3 pb-2 text-xs text-danger-text">{archiveError}</p>
            ) : null}
            <Tooltip content={deleteBlockReason} side="bottom" className="w-full">
              <button
                type="button"
                disabled={!canDelete}
                onClick={() => {
                  setDeleteError(null);
                  setConfirmOpen(true);
                }}
                className="block w-full rounded-sm px-3 py-2 text-left text-sm text-danger-text hover:bg-surface-3 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Delete form
              </button>
            </Tooltip>
          </PopoverContent>
        </Popover>
      </div>

      <DisableConfirmModal
        open={disableOpen}
        form={form}
        onClose={() => setDisableOpen(false)}
        onDisabled={() => {
          setDisableOpen(false);
          void refreshFeedbackForms();
        }}
      />

      <Modal
        open={confirmOpen}
        onClose={() => {
          if (!busy) setConfirmOpen(false);
        }}
        labelledById={`delete-form-${form.id}`}
        busy={busy}
        size="sm"
      >
        <Card>
          <Card.Header>
            <Card.Title id={`delete-form-${form.id}`}>Delete form</Card.Title>
            <Card.Description>
              Permanently delete <strong>{form.name}</strong>? This cannot be undone.
            </Card.Description>
          </Card.Header>
          {deleteError !== null ? (
            <Card.Content>
              <p className="text-sm text-danger-text">{deleteError}</p>
            </Card.Content>
          ) : null}
          <Card.Footer>
            <Button
              variant="ghost"
              onClick={() => setConfirmOpen(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => void handleDelete()}
              disabled={busy}
            >
              {busy ? 'Deleting...' : 'Delete'}
            </Button>
          </Card.Footer>
        </Card>
      </Modal>
    </li>
  );
}
